import { useMemo, useState } from "react";
import { Plus, Trash2 } from "lucide-react";

// Structured service picker — the single source of truth for job scope.
// Emits draft services: { category, label, quantity, measure?, size_key? }
export function ServiceBuilder({ catalog, onAdd }) {
  const cats = useMemo(() => {
    if (!catalog) return [];
    const measured = Object.entries(catalog.measured_categories).map(([k, v]) => ({ key: k, label: v.label, unit: v.unit, kind: "measured" }));
    const sized = Object.entries(catalog.size_categories).map(([k, v]) => ({ key: k, label: v.label, kind: "size", options: v.options }));
    const addon = { key: "trash_can_cleaning", label: "Trash Can Cleaning", kind: "trash" };
    return [...measured, ...sized, addon];
  }, [catalog]);

  const trashPrice = (n) => (n <= 0 ? 0 : n === 1 ? 25 : 40 + (n - 2) * 15);
  const FENCE_MATS = [
    { key: "vinyl", label: "Vinyl / PVC", rates: [0.2, 0.3, 0.4] },
    { key: "wood", label: "Wood", rates: [0.3, 0.45, 0.6] },
    { key: "composite", label: "Composite", rates: [0.25, 0.35, 0.45] },
    { key: "metal", label: "Metal / Aluminum / Wrought Iron", rates: [0.25, 0.35, 0.45] },
    { key: "other", label: "Other / Unknown", rates: [0.3, 0.45, 0.6] },
  ];
  const fenceArea = (l, h, s) => (Number(l) || 0) * (Number(h) || 0) * (s === "both" ? 2 : 1);
  const fencePrices = (mat, l, h, s) => {
    const m = FENCE_MATS.find((x) => x.key === mat) || FENCE_MATS[4];
    const a = fenceArea(l, h, s);
    return m.rates.map((r) => Math.max(149, Math.round(a * r)));
  };

  const [category, setCategory] = useState("");
  const [subtype, setSubtype] = useState("");
  const [measure, setMeasure] = useState("");
  const [sizeKey, setSizeKey] = useState("");
  const [qty, setQty] = useState(1);
  const [fMat, setFMat] = useState("wood");
  const [fLen, setFLen] = useState("");
  const [fHt, setFHt] = useState("");
  const [fSides, setFSides] = useState("one");
  const [fProfile, setFProfile] = useState("solid");
  const FENCE_PROFILES = [["solid", "Solid"], ["irregular", "Irregular"], ["ornamental", "Ornamental"], ["chain_link", "Chain-Link"], ["lattice", "Lattice"], ["other", "Other"]];
  const [outMode, setOutMode] = useState("area");
  const [furSub, setFurSub] = useState("");
  const [furMat, setFurMat] = useState("");
  const FURN_SUBTYPES = ["Single Chair", "Bench", "Small Table", "Large Table", "Lounge Chair", "Outdoor Sofa", "Sectional", "Table + Chair Set", "Multi-Piece Set", "Other"];
  const FURN_MATERIALS = ["Wood", "Composite", "Vinyl / Plastic", "Metal", "Wicker / Resin", "Fabric / Cushioned", "Mixed", "Other / Unknown"];

  const SUBTYPES = {
    outdoor_living: ["Porch", "Patio", "Deck", "Outdoor Seating / Living Area", "Other"],
    concrete_hardscape: ["Driveway", "Walkway", "Sidewalk", "Patio / Hardscape", "Other"],
    exterior_structures: ["Shed", "Detached Garage", "Fence", "Small Exterior Structure", "Other"],
  };

  if (cats.length === 0) return null;
  const cur = cats.find((c) => c.key === category) || null;
  const subOpts = SUBTYPES[category] || null;
  const isFence = category === "exterior_structures" && subtype === "Fence";
  const isFurniture = category === "outdoor_living" && outMode === "furniture";

  const add = () => {
    if (!cur) return;
    if (isFurniture) {
      if (!furSub) return;
      onAdd({ category: "outdoor_furniture", label: `Outdoor Furniture — ${furSub}`,
        furniture_subtype: furSub, furniture_material: furMat || "Other / Unknown", quantity: Math.max(1, Number(qty) || 1) });
      setFurSub(""); setFurMat(""); setQty(1); return;
    }
    if (cur.kind === "measured" && !(Number(measure) > 0)) return;
    if (isFence) {
      if (!(Number(fLen) > 0) || !(Number(fHt) > 0)) return;
      onAdd({ category: "fence", label: "Exterior Structures — Fence", fence_profile: fProfile,
        fence_material: fMat, fence_length: Number(fLen), fence_height: Number(fHt), fence_sides: fSides });
      setFLen(""); setFHt(""); setSubtype(""); setFProfile("solid"); return;
    }
    if (cur.kind === "trash") {
      const bins = Math.max(1, Number(qty) || 1);
      onAdd({ category: "trash_can_cleaning", label: "Trash Can Cleaning", quantity: bins });
      setQty(1); return;
    }
    const label = subOpts && subtype ? `${cur.label} — ${subtype}` : cur.label;
    const draft = { category: cur.key, label, quantity: Math.max(1, Number(qty) || 1) };
    if (cur.kind === "measured") draft.measure = Number(measure) || 0;
    if (cur.kind === "size") draft.size_key = sizeKey || cur.options[0].key;
    onAdd(draft);
    setMeasure(""); setQty(1); setSizeKey(""); setSubtype("");
  };

  return (
    <div className="grid grid-cols-2 gap-3" data-testid="service-builder">
      <div className="col-span-2">
        <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Service</label>
        <div className="grid grid-cols-2 gap-2 mt-1" data-testid="service-builder-category">
          {cats.map((c) => (
            <button key={c.key} type="button" data-testid={`service-builder-cat-${c.key}`}
              onClick={() => { setCategory(c.key); setSizeKey(""); setSubtype(""); }}
              className="sg-card-elev p-3 text-left text-sm font-semibold"
              style={{ border: category === c.key ? "2px solid var(--accent)" : "1px solid var(--brd)", cursor: "pointer" }}>
              {c.label}
            </button>
          ))}
        </div>
      </div>
      {category === "outdoor_living" && (
        <div className="col-span-2" data-testid="outdoor-mode">
          <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>What are we cleaning?</label>
          <div className="flex gap-2 mt-1">
            {[["area", "Outdoor Living AREA"], ["furniture", "Outdoor Furniture / Seating"]].map(([k, l]) => (
              <button key={k} type="button" data-testid={`outdoor-mode-${k}`} onClick={() => setOutMode(k)}
                className="chip" style={{ cursor: "pointer", background: outMode === k ? "var(--accent)" : "var(--bg-elev)", color: outMode === k ? "#fff" : "var(--txt2)", borderColor: outMode === k ? "var(--accent)" : "var(--brd)" }}>{l}</button>
            ))}
          </div>
        </div>
      )}
      {isFurniture && (
        <div className="col-span-2 grid grid-cols-2 gap-3" data-testid="furniture-builder">
          <div className="col-span-2">
            <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Furniture type</label>
            <select className="sg-input mt-1" value={furSub} onChange={(e) => setFurSub(e.target.value)} data-testid="furniture-subtype">
              <option value="">Select…</option>
              {FURN_SUBTYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Quantity</label>
            <input className="sg-input mt-1" type="number" min="1" value={qty} data-testid="furniture-qty" onChange={(e) => setQty(e.target.value)} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Material</label>
            <select className="sg-input mt-1" value={furMat} onChange={(e) => setFurMat(e.target.value)} data-testid="furniture-material">
              <option value="">Select…</option>
              {FURN_MATERIALS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          {furSub && furSub !== "Other" ? (
            <div className="col-span-2 text-xs p-2 rounded-lg" data-testid="furniture-priced-note" style={{ background: "rgba(16,185,129,0.1)", color: "#10B981" }}>
              Priced from the approved Outdoor Furniture book — the 3 service tiers will show automatically in the Estimator.
            </div>
          ) : (
            <div className="col-span-2 text-xs p-2 rounded-lg" data-testid="furniture-pending-note" style={{ background: "rgba(245,158,11,0.1)", color: "#F59E0B" }}>
              Unclassified / other furniture — routes to Manager/Owner review for a reviewed price. NOT charged at Outdoor Living area rates.
            </div>
          )}
        </div>
      )}
      {subOpts && !isFurniture && (
        <div className="col-span-2">
          <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Type</label>
          <div className="flex flex-wrap gap-2 mt-1" data-testid="service-builder-subtype">
            {subOpts.map((t) => (
              <button key={t} type="button" data-testid={`service-builder-sub-${t}`} onClick={() => setSubtype(t)}
                className="chip" style={{ cursor: "pointer", background: subtype === t ? "var(--accent)" : "var(--bg-elev)", color: subtype === t ? "#fff" : "var(--txt2)", borderColor: subtype === t ? "var(--accent)" : "var(--brd)" }}>{t}</button>
            ))}
          </div>
        </div>
      )}
      {cur?.kind === "measured" && !isFurniture && (
        <div>
          <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Measure ({cur.unit})</label>
          <input className="sg-input mt-1" type="number" min="0" value={measure} data-testid="service-builder-measure"
            onChange={(e) => setMeasure(e.target.value)} placeholder="e.g. 2000" />
        </div>
      )}
      {cur?.kind === "size" && !isFence && (
        <div>
          <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Size</label>
          <select className="sg-input mt-1" value={sizeKey || cur.options[0].key} data-testid="service-builder-size"
            onChange={(e) => setSizeKey(e.target.value)}>
            {cur.options.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
        </div>
      )}
      {isFence && (
        <div className="col-span-2 grid grid-cols-2 gap-3" data-testid="service-builder-fence">
          <div className="col-span-2">
            <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Fence profile</label>
            <div className="flex flex-wrap gap-2 mt-1">
              {FENCE_PROFILES.map(([k, l]) => (
                <button key={k} type="button" data-testid={`fence-profile-${k}`} onClick={() => setFProfile(k)}
                  className="chip" style={{ cursor: "pointer", background: fProfile === k ? "var(--accent)" : "var(--bg-elev)", color: fProfile === k ? "#fff" : "var(--txt2)", borderColor: fProfile === k ? "var(--accent)" : "var(--brd)" }}>{l}</button>
              ))}
            </div>
            {fProfile !== "solid" && (
              <div className="text-xs mt-2 p-2 rounded-lg" data-testid="fence-review-note" style={{ background: "rgba(245,158,11,0.1)", color: "#F59E0B" }}>
                Non-standard fence → routes to Manager/Owner review. Add photos + measurements. Solid-fence square-foot pricing is NOT auto-applied.
              </div>
            )}
          </div>
          <div className="col-span-2">
            <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Fence material</label>
            <div className="flex flex-wrap gap-2 mt-1">
              {FENCE_MATS.map((m) => (
                <button key={m.key} type="button" data-testid={`fence-mat-${m.key}`} onClick={() => setFMat(m.key)}
                  className="chip" style={{ cursor: "pointer", background: fMat === m.key ? "var(--accent)" : "var(--bg-elev)", color: fMat === m.key ? "#fff" : "var(--txt2)", borderColor: fMat === m.key ? "var(--accent)" : "var(--brd)" }}>{m.label}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Total length (linear ft)</label>
            <input className="sg-input mt-1" type="number" min="0" value={fLen} data-testid="fence-length" onChange={(e) => setFLen(e.target.value)} placeholder="e.g. 100" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Average height (ft)</label>
            <input className="sg-input mt-1" type="number" min="0" value={fHt} data-testid="fence-height" onChange={(e) => setFHt(e.target.value)} placeholder="e.g. 6" />
          </div>
          <div className="col-span-2">
            <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Sides being cleaned</label>
            <div className="flex gap-2 mt-1">
              {[["one", "One Side"], ["both", "Both Sides"]].map(([k, l]) => (
                <button key={k} type="button" data-testid={`fence-sides-${k}`} onClick={() => setFSides(k)}
                  className="chip" style={{ cursor: "pointer", background: fSides === k ? "var(--accent)" : "var(--bg-elev)", color: fSides === k ? "#fff" : "var(--txt2)", borderColor: fSides === k ? "var(--accent)" : "var(--brd)" }}>{l}</button>
              ))}
            </div>
          </div>
          <div className="col-span-2 text-sm p-2 rounded-lg" data-testid="fence-preview" style={{ background: "var(--bg-elev)", color: "var(--txt2)" }}>
            Cleanable area: <b style={{ color: "var(--txt)" }}>{fenceArea(fLen, fHt, fSides)} sq ft</b>
            {" · "}Essential <b style={{ color: "var(--accent)" }}>${fencePrices(fMat, fLen, fHt, fSides)[0]}</b>
            {" · "}Refresh <b style={{ color: "var(--accent)" }}>${fencePrices(fMat, fLen, fHt, fSides)[1]}</b>
            {" · "}Complete <b style={{ color: "var(--accent)" }}>${fencePrices(fMat, fLen, fHt, fSides)[2]}</b>
            <div className="text-[10px] mt-0.5">Minimum $149 · length × height × sides</div>
          </div>
        </div>
      )}
      {cur?.kind === "trash" && (
        <div className="col-span-2 grid grid-cols-2 gap-3 items-end">
          <div>
            <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Number of bins</label>
            <input className="sg-input mt-1" type="number" min="1" value={qty} data-testid="service-builder-bins"
              onChange={(e) => setQty(e.target.value)} placeholder="e.g. 3" />
          </div>
          <div className="text-sm" data-testid="service-builder-trash-preview" style={{ color: "var(--txt2)" }}>
            Service Total: <b style={{ color: "var(--accent)" }}>${trashPrice(Math.max(1, Number(qty) || 1))}</b>
            <div className="text-[10px]">1 bin $25 · 2 bins $40 · +$15 each additional</div>
          </div>
        </div>
      )}
      {cur && cur.kind !== "trash" && !isFence && (
        <div>
          <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Quantity</label>
          <input className="sg-input mt-1" type="number" min="1" value={qty} data-testid="service-builder-qty"
            onChange={(e) => setQty(e.target.value)} />
        </div>
      )}
      <div className="col-span-2">
        <button className="sg-btn sg-btn-accent w-full" disabled={!cur || (isFurniture && !furSub) || (!isFurniture && cur.kind === "measured" && !(Number(measure) > 0)) || (isFence && !(Number(fLen) > 0 && Number(fHt) > 0))} onClick={add} data-testid="service-builder-add">
          <Plus size={16} /> Add Service
        </button>
      </div>
    </div>
  );
}

// Read-only / removable list of pending draft services (used before a job exists).
export function ServiceDraftList({ services, onRemove }) {
  if (services.length === 0) return <div className="text-sm" style={{ color: "var(--txt2)" }} data-testid="service-draft-empty">No services added yet.</div>;
  return (
    <div className="flex flex-col gap-2" data-testid="service-draft-list">
      {services.map((s, i) => (
        <div key={i} className="flex items-center justify-between sg-card-elev p-3" data-testid={`service-draft-${i}`}>
          <div className="text-sm font-semibold">
            {s.label}{s.quantity > 1 ? ` × ${s.quantity}` : ""}
            <span className="text-xs ml-2" style={{ color: "var(--txt2)" }}>
              {s.category === "trash_can_cleaning" ? `${s.quantity} bins`
                : s.category === "fence" ? `${s.fence_material} · ${s.fence_length} LF × ${s.fence_height} ft × ${s.fence_sides === "both" ? "2 sides" : "1 side"}`
                : s.measure ? `${s.measure} sq/ln ft` : s.size_key ? s.size_key : ""}
            </span>
          </div>
          {onRemove && <button onClick={() => onRemove(i)} data-testid={`service-draft-remove-${i}`}><Trash2 size={16} style={{ color: "var(--danger)" }} /></button>}
        </div>
      ))}
    </div>
  );
}
